import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/api-client.js";

export interface AddressSuggestion {
  lat: number;
  lng: number;
  /** Kurzes Label für die Liste, z.B. "Musterstraße 1, 4020 Linz". */
  label: string;
  displayName: string;
  confidence: number;
}

/**
 * Liefert debounced Adressvorschläge (Nominatim via Backend-Proxy) für eine
 * laufende Eingabe. Veraltete Antworten werden über eine Sequenznummer verworfen,
 * damit langsame frühere Requests keine neueren Ergebnisse überschreiben.
 */
export function useAddressSuggest(
  query: string,
  opts: { minLength?: number; debounceMs?: number; enabled?: boolean } = {},
) {
  const { minLength = 3, debounceMs = 400, enabled = true } = opts;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < minLength) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const mySeq = ++seq.current;
    const handle = setTimeout(() => {
      void apiClient
        .get<AddressSuggestion[]>(`/geo/geocode/suggest?q=${encodeURIComponent(q)}`)
        .then((res) => {
          if (mySeq !== seq.current) return; // veraltet → verwerfen
          setSuggestions(res.success && Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => {
          if (mySeq === seq.current) setSuggestions([]);
        })
        .finally(() => {
          if (mySeq === seq.current) setLoading(false);
        });
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [query, minLength, debounceMs, enabled]);

  return { suggestions, loading };
}
